import type { MovementSessionModel } from "./movements.model";

/**
 * WHERE AN ATHLETE STANDS ON ONE LIFT — the arithmetic behind the row that says it.
 *
 * Pure, no React, no formatting, so every decision here is testable against fixed numbers
 * rather than against a rendering. The WORDING of the row is the component's, because only it
 * knows the athlete's unit and language.
 *
 * ═══ 🚨 WHY `schemeOf` MOVED HERE RATHER THAN BEING WRITTEN A SECOND TIME ═══
 *
 * It was private to `MovementSessions.tsx`, and the last-time row needs the same sentence —
 * `5 × 5` — about the same session. Two spellings of one scheme is precisely the bug this whole
 * set of work has been removing: the day the session card starts counting derived lines and the
 * log row does not, the same session reads two different ways on two screens and nothing fails.
 */

/**
 * THE SCHEME AS PERFORMED — `5 × 5`, and only when that is true of every set.
 *
 * 🚨 LOGGED LINES ONLY. A derived metcon line carries a whole block's rep total against the
 * weight the workout chose; counting one would turn a day of three heavy triples plus a metcon
 * into "4 × 3", which is a set the athlete never did.
 *
 * ⚠️ AND IT FALLS BACK TO A COUNT RATHER THAN INVENTING A REPRESENTATIVE REP. `5 × 5` is a claim
 * about all five sets, so it is printed only when all five agree; a day of 5, 5, 5, 3, 3 reads
 * "5 sets" and the rows underneath say the rest. Averaging or taking the first would put a
 * scheme on screen that no line of the session matches.
 */
export function schemeOf(session: MovementSessionModel): { sets: number; reps: number | null } {
  const logged = session.lines.filter((line) => line.source === "logged");
  const first = logged[0]?.reps ?? null;
  const uniform = first !== null && logged.every((line) => line.reps === first);
  return { sets: logged.length, reps: uniform ? first : null };
}

/** The day, the scheme and the load of the most recent session this movement appeared in. */
export interface LastTimeModel {
  /** `YYYY-MM-DD` in the athlete's own calendar. A civil date; parsed only in the converter. */
  performedOn: string;
  sets: number;
  /** Reps per set, when every logged set agrees. Null means "print the count instead". */
  reps: number | null;
  /**
   * THE HEAVIEST SET A PERSON ENTERED THAT DAY, in KILOS. Null where they entered none — an
   * unloaded movement, or a day that was only a metcon appearance.
   *
   * 🚨 IT IS THE SERVICE'S `topLoadKg` AND IS NEVER RE-DERIVED FROM THE LINES. The service
   * computes it over the measured sets only; taking a maximum over `lines` here would let a
   * derived metcon line — which carries the weight the WORKOUT chose — become the number this
   * row prints as what the athlete put on the bar.
   */
  topLoadKg: number | null;
  /** The total count in the movement's own unit, for a day nobody put a weight on. */
  totalReps: number;
}

/**
 * THE LAST TIME THIS MOVEMENT WAS TRAINED.
 *
 * ⚠️ THE FIRST ITEM, BECAUSE THE LIST IS NEWEST FIRST — not a scan for a maximum date. The
 * service decides the order (`movements/routes.ts` pages it descending) and a client that
 * re-sorted would be a second opinion about which day is the most recent, disagreeing with the
 * drawer directly underneath it the first time two sessions share a day.
 *
 * Null for an athlete who has never logged this movement — which is a real and common state,
 * and is the first session of a new lift.
 */
export function lastTimeOf(sessions: readonly MovementSessionModel[]): LastTimeModel | null {
  const latest = sessions[0];
  if (latest === undefined) return null;

  const scheme = schemeOf(latest);
  return {
    performedOn: latest.performedOn,
    sets: scheme.sets,
    reps: scheme.reps,
    topLoadKg: latest.topLoadKg,
    totalReps: latest.reps,
  };
}

/**
 * THE SERIES THE SPARKLINE DRAWS — one value per bucket, nulls kept.
 *
 * 🚨 THE NULLS SURVIVE AND ARE NOT FILTERED OUT. A bucket where the movement was trained but
 * nothing carried a measurable weight has no estimate, and dropping it would slide every later
 * point one step left — drawing a smooth run of progress across a month that contains no such
 * number. The chart breaks its line there instead, exactly as the full-size trend does.
 */
export function standingSeries(
  points: readonly { periodStart: string; estimatedMaxKg: number | null }[],
): { periodStart: string; value: number | null }[] {
  return points.map((point) => ({ periodStart: point.periodStart, value: point.estimatedMaxKg }));
}

/**
 * IS THERE ENOUGH TO DRAW A LINE AT ALL?
 *
 * ⚠️ ONE POINT IS NOT A CHART, and in a row this small it is worse than nothing: a single dot
 * floating in a 28px box reads as a chart that failed to load, mid-session, to somebody who has
 * four seconds to look at it. Two is the floor, the same floor `hasSeries` sets for the full
 * chart — stated separately because this row has no copy to fall back to, it simply draws
 * nothing.
 */
export function canDrawSparkline(
  series: readonly { value: number | null }[],
): boolean {
  return series.filter((row) => row.value !== null).length >= 2;
}
